import { useState } from "react";
import { createPortal } from "react-dom";
import {
  getClassicModels,
  getGenerationCreditCost,
  INTEGRATED_MODEL,
  INTEGRATED_MODEL_ID,
  AFFILIATE_DEFAULT_MODEL_ID,
  INTEGRATED_DURATION_MIN,
  INTEGRATED_DURATION_MAX,
  POWER_TIER_LABEL,
  CLASSIC_TIER_LABEL,
  isClassicSilentModel,
  type ModelId,
  type DurationOption,
} from "@/config/models";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/lib/utils";
import { IconCoin } from "@/components/ui/cartoon-icons";
import { Zap, Gem, Crown, Sparkles, Lock, AudioLines } from "lucide-react";
import { Link } from "react-router-dom";

interface ModelSelectorProps {
  value: ModelId;
  duration: DurationOption;
  onChange: (id: ModelId) => void;
  onDurationChange: (d: DurationOption) => void;
  integratedSeconds: number;
  onIntegratedSecondsChange: (seconds: number) => void;
  /** Affiliate free-credit path — only Classic Standard 5s is selectable. */
  lockToAffiliateDefault?: boolean;
  /**
   * When set (signed-out traffic), locked-model taps open Sign Up instead of
   * the purchase upsell — anonymous users can't buy a pack yet.
   */
  onRequireAuth?: () => void;
}

export function ModelSelector({
  value,
  duration,
  onChange,
  onDurationChange,
  integratedSeconds,
  onIntegratedSecondsChange,
  lockToAffiliateDefault = false,
  onRequireAuth,
}: ModelSelectorProps) {
  const { hasPurchased } = useUserStore();
  const powerLocked = !hasPurchased;
  const isIntegrated = value === INTEGRATED_MODEL_ID;
  const isClassic = isClassicSilentModel(value);
  const [showUpgradeModal, setShowUpgradeModal] = useState<"power" | "affiliate" | "8s" | null>(null);

  const classicModels = getClassicModels(duration);
  const integratedCost = getGenerationCreditCost(INTEGRATED_MODEL, integratedSeconds);

  const gateOrUpsell = (kind: "power" | "affiliate" | "8s") => {
    if (onRequireAuth) {
      onRequireAuth();
      return;
    }
    setShowUpgradeModal(kind);
  };

  const selectModel = (id: ModelId) => {
    if (id === INTEGRATED_MODEL_ID && powerLocked) {
      gateOrUpsell("power");
      return;
    }
    if (lockToAffiliateDefault && id !== AFFILIATE_DEFAULT_MODEL_ID) {
      gateOrUpsell("affiliate");
      return;
    }
    onChange(id);
  };

  const switchDuration = (d: DurationOption) => {
    if (lockToAffiliateDefault && d !== "5s") {
      gateOrUpsell("affiliate");
      return;
    }
    if (d === duration) return;
    onDurationChange(d);
    if (!isClassicSilentModel(value)) return;
    const currentTier = getClassicModels(duration).find((m) => m.id === value)?.tier ?? "Standard";
    const match = getClassicModels(d).find((m) => m.tier === currentTier);
    if (match) onChange(match.id);
  };

  const handle8sClick = () => {
    if (lockToAffiliateDefault) gateOrUpsell("affiliate");
    else if (!hasPurchased) gateOrUpsell("8s");
    else switchDuration("8s");
  };

  const upgradeCopy =
    showUpgradeModal === "power"
      ? {
          title: `Unlock ${POWER_TIER_LABEL}`,
          body: (
            <>Purchase any token pack to unlock <strong className="text-[#241d3a]">{POWER_TIER_LABEL}</strong> — 720p video with native audio, 5–15s.</>
          ),
        }
      : showUpgradeModal === "affiliate"
        ? {
            title: "Unlock More Models",
            body: (
              <>Your free tokens cover <strong className="text-[#241d3a]">Classic Standard 5s</strong>. Purchase any pack to unlock {POWER_TIER_LABEL}, Premium, and 8s.</>
            ),
          }
        : {
            title: "Unlock 8-Second Videos",
            body: (
              <>Purchase any token pack to unlock <strong className="text-[#241d3a]">8-second</strong> classic generations.</>
            ),
          };

  return (
    <div className="space-y-4">
      {lockToAffiliateDefault && (
        <p className="text-[11px] text-[#241d3a]/50 leading-relaxed font-semibold">
          {onRequireAuth ? (
            <>
              Free credits cover <span className="text-[#241d3a] font-extrabold">Classic Standard 5s</span>
              . Sign up free to generate — unlock {POWER_TIER_LABEL} after any pack.
            </>
          ) : (
            <>
              Free credits cover <span className="text-[#241d3a] font-extrabold">Classic Standard 5s</span>
              . Buy a pack to unlock other models.
            </>
          )}
        </p>
      )}

      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#241d3a]/45 mb-2">
          {POWER_TIER_LABEL}
        </p>
        <button
          type="button"
          onClick={() => selectModel(INTEGRATED_MODEL_ID)}
          className={cn(
            "group relative w-full overflow-hidden rounded-2xl border-2 text-left transition-all",
            isIntegrated
              ? "border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.2)]"
              : "border-[#241d3a]/20 hover:border-[#7c5cf0]/50",
          )}
          style={{
            background: isIntegrated
              ? "linear-gradient(135deg, #c4b4f5 0%, #b8a5f0 40%, #9b84e8 100%)"
              : "linear-gradient(135deg, rgba(184,165,240,0.35) 0%, rgba(36,29,58,0.06) 55%, rgba(36,29,58,0.04) 100%)",
          }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 12% 0%, rgba(255,255,255,0.45), transparent 40%), radial-gradient(ellipse at 100% 100%, rgba(124,92,240,0.2), transparent 45%)",
            }}
          />
          <div className="relative flex items-center gap-3 p-3.5">
            <div
              className={cn(
                "size-10 rounded-xl flex items-center justify-center shrink-0 border-2",
                isIntegrated
                  ? "bg-white border-[#2a2342] text-[#7c5cf0] shadow-[2px_2px_0_rgba(42,35,66,0.12)]"
                  : "bg-white/80 border-[#241d3a]/15 text-[#7c5cf0]/70",
              )}
            >
              {powerLocked ? <Lock className="size-4" strokeWidth={2} /> : <AudioLines className="size-4" strokeWidth={2} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={cn("text-[13px] font-extrabold", isIntegrated ? "text-[#241d3a]" : "text-[#241d3a]/75")}>
                  {INTEGRATED_MODEL.label}
                </p>
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-white bg-[#7c5cf0] px-1.5 py-0.5 rounded">
                  PRO
                </span>
              </div>
              <p className="text-[11px] text-[#241d3a]/55 mt-0.5 flex items-center gap-1.5 font-semibold">
                <Sparkles className="size-3 shrink-0 text-[#7c5cf0]" strokeWidth={2} />
                native audio · {INTEGRATED_DURATION_MIN}–{INTEGRATED_DURATION_MAX}s
              </p>
            </div>
            {isIntegrated && !powerLocked ? (
              <span className="text-[12px] font-extrabold text-[#241d3a] tabular-nums shrink-0 inline-flex items-center gap-1">
                <IconCoin className="size-3" />{integratedCost}
              </span>
            ) : powerLocked ? (
              <span className="text-[10px] font-extrabold text-[#7c5cf0] shrink-0">Unlock</span>
            ) : null}
          </div>
        </button>

        {isIntegrated && !powerLocked && (
          <div className="mt-2 rounded-2xl border-2 border-[#b8a5f0]/50 bg-[#b8a5f0]/15 p-3.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#241d3a]/45">Clip length</p>
              <p className="text-[13px] font-extrabold text-[#241d3a] tabular-nums inline-flex items-center gap-1">
                {integratedSeconds}s
                <span className="text-[#241d3a]/50 font-bold ml-1 inline-flex items-center gap-0.5">
                  · <IconCoin className="size-3" />{integratedCost}
                </span>
              </p>
            </div>
            <input
              type="range"
              min={INTEGRATED_DURATION_MIN}
              max={INTEGRATED_DURATION_MAX}
              step={1}
              value={integratedSeconds}
              onChange={(e) => onIntegratedSecondsChange(Number(e.target.value))}
              className="w-full accent-[#7c5cf0] h-1.5 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-[#241d3a]/40 tabular-nums">
              <span>{INTEGRATED_DURATION_MIN}s</span>
              <span>{INTEGRATED_DURATION_MAX}s</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#241d3a]/45 mb-2">
          {CLASSIC_TIER_LABEL}
        </p>
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => switchDuration("5s")}
              className={cn(
                "flex-1 px-3 py-2 rounded-2xl border-2 text-[12px] font-extrabold transition-all",
                duration === "5s" && isClassic
                  ? "bg-[#b8a5f0] border-[#2a2342] text-[#241d3a]"
                  : "bg-[#241d3a]/[0.05] border-[#241d3a]/15 text-[#241d3a]/50",
              )}
            >
              5s
            </button>
            <button
              type="button"
              onClick={handle8sClick}
              className={cn(
                "flex-1 px-3 py-2 rounded-2xl border-2 text-[12px] font-extrabold transition-all flex items-center justify-center gap-1",
                duration === "8s" && isClassic
                  ? "bg-[#b8a5f0] border-[#2a2342] text-[#241d3a]"
                  : "bg-[#241d3a]/[0.05] border-[#241d3a]/15 text-[#241d3a]/50",
                (lockToAffiliateDefault || !hasPurchased) && "opacity-70",
              )}
            >
              <Crown className="size-3 text-[#7c5cf0]" strokeWidth={2} />
              8s
              {!hasPurchased && (
                <span className="text-[9px] font-extrabold text-[#7c5cf0] bg-[#b8a5f0]/40 px-1 py-0.5 rounded">PRO</span>
              )}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {classicModels.map((model) => {
              const Icon = model.tier === "Premium" ? Gem : Zap;
              const isSelected = value === model.id;
              const locked = lockToAffiliateDefault && model.id !== AFFILIATE_DEFAULT_MODEL_ID;
              return (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => selectModel(model.id)}
                  className={cn(
                    "relative flex flex-col items-center p-3 rounded-2xl border-2 transition-all text-center",
                    isSelected
                      ? "bg-[#b8a5f0] border-[#2a2342]"
                      : "bg-[#241d3a]/[0.05] border-[#241d3a]/15 hover:bg-[#241d3a]/[0.08]",
                    locked && "opacity-60",
                  )}
                >
                  {locked && (
                    <Lock className="absolute top-2 right-2 size-3 text-[#241d3a]/40" strokeWidth={2} />
                  )}
                  <Icon className={cn("size-3.5 mb-1", isSelected ? "text-[#7c5cf0]" : "text-[#241d3a]/40")} strokeWidth={2} />
                  <span className={cn("text-[12px] font-extrabold", isSelected ? "text-[#241d3a]" : "text-[#241d3a]/55")}>
                    {model.label}
                  </span>
                  <span className="text-[10px] text-[#241d3a]/45 tabular-nums inline-flex items-center gap-0.5">
                    <IconCoin className="size-2.5" />{model.creditCost}
                  </span>
                </button>
              );
            })}
          </div>

          {hasPurchased && isClassic && (
            <button
              type="button"
              onClick={() => onChange(INTEGRATED_MODEL_ID)}
              className="mt-1 flex w-full items-start gap-2.5 rounded-2xl border-2 border-[#b8a5f0]/50 bg-[#b8a5f0]/15 p-2.5 text-left transition-colors hover:bg-[#b8a5f0]/25"
            >
              <Sparkles className="size-3.5 shrink-0 text-[#7c5cf0] mt-0.5" strokeWidth={2} />
              <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-[#241d3a]/70 font-semibold">
                Try <span className="font-extrabold text-[#241d3a]">{POWER_TIER_LABEL}</span> for native audio · 5–15s
              </span>
              <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-[#7c5cf0]">
                Switch
              </span>
            </button>
          )}
        </div>
      </div>

      {showUpgradeModal && createPortal(
        <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-md" onClick={() => setShowUpgradeModal(null)} style={{ zIndex: 0 }} />
          <div
            className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden shadow-2xl border-2 border-[#241d3a]/15 animate-modal-in bg-white"
            style={{ zIndex: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-7 pb-5 text-center">
              <div className="size-14 rounded-2xl bg-[#b8a5f0]/20 border-2 border-[#241d3a]/15 flex items-center justify-center mx-auto mb-4">
                <Crown className="size-7 text-[#7c5cf0]" />
              </div>
              <h3 className="text-xl font-extrabold text-[#241d3a] mb-1">{upgradeCopy.title}</h3>
              <p className="text-[14px] text-[#241d3a]/60 leading-relaxed">{upgradeCopy.body}</p>
            </div>
            <div className="px-6 pb-6 flex flex-col gap-2">
              <Link to="/billing" className="btn-primary w-full py-3 rounded-xl text-[14px] text-center flex items-center justify-center gap-2">
                <Sparkles className="size-4" />
                Get Tokens
              </Link>
              <button
                onClick={() => setShowUpgradeModal(null)}
                className="w-full py-2.5 rounded-xl text-[13px] font-semibold text-[#241d3a]/50 hover:text-[#241d3a] hover:bg-[#241d3a]/5 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
